import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Typography from '@material-ui/core/Typography';

import Tooltip from 'components/common/Tooltip';
import IconInfo from 'components/icons/IconInfo';
import IconButton from 'components/common/IconButton';
import IconMagnifier from 'components/icons/IconMagnifier';
import IconCross from 'components/icons/IconCross';
import IconWatch from 'components/icons/IconWatch';

import * as fromFieldActions from 'redux/modules/formControl';

import pxToRem from 'styles/pxToRem';
import pxToEm from 'styles/pxToEm';
import hexToRgb from 'styles/hexToRgb';

const styles = theme => ({
  formControl: {
    margin: 0,
  },
  formControlErrorVisible: {
    zIndex: 10,
  },
  inputRoot: {
    border: `1px solid ${theme.palette.grey[600]}`,
    borderRadius: 4,
    'label + &': {
      marginTop: 0,
    },
  },
  inputRootColorDark: {
    backgroundColor: theme.palette.grey[100],
  },
  inputRootColorLight: {
    backgroundColor: theme.palette.common.white,
  },
  inputRootSearch: {
    border: 'none',
    backgroundColor: 'transparent',
  },
  inputInput: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.5, 14),
    lineHeight: pxToEm(16, 14),
    height: pxToEm(16, 14),
    padding: '9px 8px 10px 8px',
    color: theme.palette.secondary[800],
    '&::placeholder': {
      opacity: 1,
      color: `rgba(${hexToRgb(theme.palette.text.primary)}, 0.54)`,
    },
    '&::-webkit-search-cancel-button': {
      display: 'none',
    },
    '&::-ms-clear': {
      display: 'none',
    },
  },
  inputDisabled: {
    color: `rgba(${hexToRgb(theme.palette.secondary[800])}, 0.4)`,
    backgroundColor: theme.palette.grey[100],
    '& $inputDisabled': {
      backgroundColor: 'transparent',
    },
  },
  inputInputSearch: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
    lineHeight: pxToEm(24, 16),
    height: pxToEm(24, 16),
    paddingTop: 10,
    paddingRight: 5,
  },
  inputInputSizeXs: {
    padding: '2px 10px',
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.25, 13),
    lineHeight: pxToEm(18, 13),
    height: pxToEm(18, 13),
  },
  inputInputSizeSm: {
    padding: '5px 10px',
    fontSize: pxToRem(13),
    letterSpacing: pxToEm(0.25, 13),
    lineHeight: pxToEm(18, 13),
    height: pxToEm(18, 13),
  },
  inputInputSizeLg: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.5, 16),
    lineHeight: pxToEm(19, 16),
    height: pxToEm(19, 16),
    padding: '14px 24px',
  },
  inputFocused: {
    borderColor: theme.palette.primary[500],
    caretColor: theme.palette.primary[500],
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.1)`,
  },
  inputFullWidth: {
    width: 'calc(100% - 2px)',
  },
  inputMultiline: {
    paddingTop: 12,
    paddingBottom: 15,
  },
  inputInputMultiline: {
    paddingTop: 0,
    paddingBottom: 0,
    boxSizing: 'border-box',
    height: '100%',
  },
  inputInputMultilineAutorows: {
    overflow: 'hidden',
  },
  label: {
    position: 'relative',
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
    fontSize: pxToRem(12),
    letterSpacing: pxToEm(0.5),
    fontWeight: 700,
    color: theme.palette.grey[600],
    transform: 'none',
  },
  labelSizeLg: {
    fontSize: pxToRem(14),
    fontWeight: 400,
    marginBottom: 15,
  },
  labelHidden: {
    visibility: 'hidden',
  },
  labelOverlap: {
    marginBottom: -6,
    marginLeft: 4,
    width: 'fit-content',
    zIndex: 2,
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      right: -1,
      left: -1,
      bottom: 5,
      height: 2,
      backgroundColor: theme.palette.common.white,
      zIndex: -1,
    },
  },
  labelWithAdornment: {
    display: 'inline-block',
  },
  inputError: {
    borderColor: theme.palette.validation[500],
  },
  iconInfo: {
    display: 'block',
    width: 14,
    height: 14,
  },
  iconMagnifier: {
    width: 18,
    height: 18,
  },
  iconMagnifierStart: {
    marginRight: 10,
  },
  iconMagnifierEnd: {
    marginLeft: 10,
  },
  iconCross: {
    width: 12,
    height: 12,
  },
  buttonClearStart: {
    margin: '-18px -5px -18px -15px',
  },
  buttonClearEnd: {
    margin: '-18px -15px -18px -5px',
    position: 'relative',
  },
  labelNowrap: {
    whiteSpace: 'nowrap',
  },
  iconWatchLine: {
    position: 'absolute',
    top: 0,
    left: 23,
    width: 2,
    height: 24,
    backgroundColor: theme.palette.grey[600],
    transform: 'rotate(-45deg)',
  },
});

class TextField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      hovered: false,
      blurred: true,
      descriptionOpen: false,
      passwordVisible: false,
    };

    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.onDescriptionOpen = this.onDescriptionOpen.bind(this);
    this.onDescriptionClose = this.onDescriptionClose.bind(this);
    this.onPasswordVisibilityButtonClick = this.onPasswordVisibilityButtonClick.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { meta: { touched, error, active }, input: { value }, disabled } = this.props;
    if (nextProps.input.value !== value
      || nextProps.meta.touched !== touched
      || nextProps.meta.error !== error
      || nextProps.meta.active !== active
      || nextProps.disabled !== disabled) {
      return true;
    }
    return false;
  }

  onMouseOver() {
    this.setState({ hovered: true });
  }

  onMouseOut() {
    this.setState({ hovered: false });
  }

  onChange(event) {
    if (this.state.blurred === true) {
      this.setState({ blurred: false });
    }
    this.props.input.onChange(event);
    this.props.onChange(event);
  }

  onFocus(event) {
    this.props.input.onFocus(event);
    if (this.props.meta.touched && Boolean(this.props.meta.error)) {
      this.props.actions.focusFormControl(this.props.input.name);
    }
    this.props.onFocus(event);
  }

  onBlur(event) {
    this.setState({ blurred: true });
    this.props.input.onBlur(event);
    this.props.actions.blurFormControl();
    this.props.onBlur(event);
  }

  onDescriptionOpen() {
    this.setState({ descriptionOpen: true });
  }

  onDescriptionClose() {
    this.setState({ descriptionOpen: false });
  }

  onClearButtonClick() {
    this.props.input.onChange('');
    if (this.inputComponentRef) {
      this.inputComponentRef.focus();
    }
  }

  onPasswordVisibilityButtonClick() {
    this.setState({
      passwordVisible: !this.state.passwordVisible,
    });
    this.inputComponentRef.focus();
  }

  render() {
    const {
      id,
      placeholder,
      type,
      label,
      labelEndAdornment,
      className,
      inputRootClassName,
      fullWidth,
      classes,
      multiline,
      hideLabel,
      size,
      color,
      disabled,
      input,
      meta: { touched, error, active },
      formControl,
      inputRef,
      onKeyDown,
      onClick,
      endAdornment,
      startAdornment,
      errorPlacement,
      description,
      descriptionPlacement,
      maxLength,
      rows,
      startAdornmentClearButton,
      endAdornmentClearButton,
      tabIndex,
      labelOverlap,
      focused,
      autoComplete,
      labelNowrap,
    } = this.props;
    const { hovered, blurred, descriptionOpen, passwordVisible } = this.state;
    const errorVisible = (active && touched && Boolean(error) && !descriptionOpen) ||
      (hovered && touched && Boolean(error) && !formControl && !descriptionOpen);

    const descriptionAdornment = (
      <InputAdornment position="end">
        <Tooltip
          content={
            <Typography variant="subheading" color="secondary">
              {description}
            </Typography>
          }
          placement={descriptionPlacement}
          color="grey"
          onOpen={this.onDescriptionOpen}
          onClose={this.onDescriptionClose}
        >
          <IconInfo className={classes.iconInfo} />
        </Tooltip>
      </InputAdornment>
    );
    const labelElement = label ? (
      <InputLabel
        className={classNames(classes.label, {
          [classes.labelHidden]: hideLabel,
          [classes.labelWithAdornment]: labelEndAdornment,
          [classes.labelSizeLg]: size === 'lg',
          [classes.labelOverlap]: labelOverlap === true,
          [classes.labelNowrap]: labelNowrap === true,
        })}
        disableAnimation
        htmlFor={input.name}
        shrink={false}
      >
        {label}
      </InputLabel>
    ) : null;
    return (
      <FormControl
        fullWidth={fullWidth}
        className={classNames(className, classes.formControl, {
          [classes.formControlErrorVisible]: errorVisible,
        })}
        error={touched && Boolean(error) && blurred}
      >
        {labelEndAdornment ? (
          <div>
            {label &&
              labelElement
            }
            {labelEndAdornment}
          </div>
        ) : (
          <Fragment>
            {label &&
              labelElement
            }
          </Fragment>
        )}
        
        <Tooltip
          content={
            error ? (
              <Typography variant="subheading" color="secondary">
                {typeof error === 'object' ? JSON.stringify(error) : error}
              </Typography>
            ) : ' '
          }
          open={errorVisible}
          placement={errorPlacement}
          color="white"
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <Input
            id={id}
            name={input.name}
            type={type === 'search' ? 'text' : type}
            {...passwordVisible ? {
              type: 'text',
            } : {}}
            disableUnderline
            fullWidth={fullWidth}
            multiline={multiline}
            {...rows ? { rows } : {}}
            placeholder={placeholder}
            inputRef={(inputComponentRef) => {
              this.inputComponentRef = inputComponentRef;
              inputRef(inputComponentRef);
            }}
            classes={{
              root: classNames(classes.inputRoot, inputRootClassName, {
                [classes.inputRootColorDark]: color === 'dark',
                [classes.inputRootColorLight]: color === 'light',
                [classes.inputRootSearch]: type === 'search',
                [classes.inputFocused]: focused,
              }),
              input: classNames(classes.inputInput, {
                [classes.inputInputSearch]: type === 'search',
                [classes.inputInputSizeXs]: size === 'xs',
                [classes.inputInputSizeSm]: size === 'sm',
                [classes.inputInputSizeLg]: size === 'lg',
              }),
              focused: classes.inputFocused,
              fullWidth: classes.inputFullWidth,
              multiline: classes.inputMultiline,
              inputMultiline: classNames(classes.inputInputMultiline, {
                [classes.inputInputMultilineAutorows]: !rows,
              }),
              disabled: classes.inputDisabled,
              error: classes.inputError,
            }}
            {...input}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onKeyDown={onKeyDown}
            onClick={onClick}
            {...startAdornmentClearButton ? { startAdornment: (
              <InputAdornment
                position="start"
                className={classes.inputAdorment}
              >
                {input.value.length > 0 ? (
                  <IconButton
                    className={classes.buttonClearStart}
                    onClick={this.onClearButtonClick}
                    tooltipContent="Clear Value"
                  >
                    <IconCross className={classes.iconCross} />
                  </IconButton>
                ) : (
                  <IconMagnifier
                    className={classNames(classes.iconMagnifier, classes.iconMagnifierStart)}
                  />
                )}
              </InputAdornment>
            ) } : { startAdornment }}
            {...endAdornmentClearButton ? { endAdornment: (
              <InputAdornment
                position="end"
                className={classes.inputAdorment}
              >
                {input.value.length > 0 ? (
                  <IconButton
                    className={classes.buttonClearEnd}
                    onClick={this.onClearButtonClick}
                    tooltipContent="Clear Value"
                  >
                    <IconCross className={classes.iconCross} />
                  </IconButton>
                ) : (
                  <IconMagnifier
                    className={classNames(classes.iconMagnifier, classes.iconMagnifierEnd)}
                  />
                )}
              </InputAdornment>
            ) } : {}}
            {...type === 'password' ? { endAdornment: (
              <InputAdornment
                position="end"
                className={classes.inputAdorment}
              >
                {input.value.length > 0 &&
                  <IconButton
                    className={classes.buttonClearEnd}
                    onClick={this.onPasswordVisibilityButtonClick}
                    tooltipContent={passwordVisible ? 'Hide Password' : 'Show Password'}
                  >
                    <IconWatch className={classes.iconWatch} />
                    {passwordVisible &&
                      <div className={classes.iconWatchLine} />
                    }
                  </IconButton>
                }
              </InputAdornment>
            )} : {}}
            {...description ? { endAdornment: descriptionAdornment } : {}}
            {...endAdornment ? { endAdornment } : {}}
            inputProps={{
              onMouseOver: this.onMouseOver,
              onMouseOut: this.onMouseOut,
              maxLength,
              autoComplete,
              ...tabIndex ? { tabIndex } : {},
            }}
            disabled={disabled}
          />
        </Tooltip>
      </FormControl>
    );
  }
}

TextField.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.node,
  labelEndAdornment: PropTypes.node,
  className: PropTypes.string,
  inputRootClassName: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  maxLength: PropTypes.string,
  fullWidth: PropTypes.bool,
  multiline: PropTypes.bool,
  hideLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg']),
  color: PropTypes.oneOf(['light', 'dark']),
  formControl: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  inputRef: PropTypes.func,
  endAdornment: PropTypes.node,
  startAdornment: PropTypes.node,
  errorPlacement: PropTypes.oneOf(['right', 'left', 'top', 'bottom']),
  descriptionPlacement: PropTypes.oneOf(['right', 'left', 'top', 'bottom']),
  rows: PropTypes.number,
  startAdornmentClearButton: PropTypes.bool,
  endAdornmentClearButton: PropTypes.bool,
  tabIndex: PropTypes.number,
  autocomplete: PropTypes.string,
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  actions: PropTypes.shape({
    focusFormControl: PropTypes.func.isRequired,
    blurFormControl: PropTypes.func.isRequired,
  }).isRequired,
  focused: PropTypes.bool,
  labelOverlap: PropTypes.bool,
  labelNowrap: PropTypes.bool,
  classes: PropTypes.shape({
    formControl: PropTypes.string.isRequired,
    formControlErrorVisible: PropTypes.string.isRequired,
    inputRoot: PropTypes.string.isRequired,
    inputRootColorDark: PropTypes.string.isRequired,
    inputRootColorLight: PropTypes.string.isRequired,
    inputRootSearch: PropTypes.string.isRequired,
    inputInput: PropTypes.string.isRequired,
    inputInputSearch: PropTypes.string.isRequired,
    inputFocused: PropTypes.string.isRequired,
    inputFullWidth: PropTypes.string.isRequired,
    inputMultiline: PropTypes.string.isRequired,
    inputInputMultiline: PropTypes.string.isRequired,
    inputInputMultilineAutorows: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    inputError: PropTypes.string.isRequired,
    iconMagnifier: PropTypes.string.isRequired,
    iconMagnifierStart: PropTypes.string.isRequired,
    iconMagnifierEnd: PropTypes.string.isRequired,
    iconCross: PropTypes.string.isRequired,
    buttonClearStart: PropTypes.string.isRequired,
    buttonClearEnd: PropTypes.string.isRequired,
  }).isRequired,
};

TextField.defaultProps = {
  placeholder: '',
  type: 'text',
  label: null,
  labelEndAdornment: null,
  className: '',
  inputRootClassName: '',
  fullWidth: false,
  multiline: false,
  hideLabel: false,
  disabled: false,
  size: 'md',
  color: 'light',
  formControl: null,
  startAdornment: null,
  endAdornment: null,
  description: null,
  errorPlacement: 'right',
  descriptionPlacement: 'right',
  maxLength: '255',
  rows: null,
  tabIndex: null,
  startAdornmentClearButton: false,
  endAdornmentClearButton: false,
  focused: false,
  labelOverlap: false,
  labelNowrap: false,
  autoComplete: 'off',
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onKeyDown: () => {},
  onClick: () => {},
  inputRef: () => {},
};

function mapStateToProps(state) {
  return {
    formControl: state.formControl,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(fromFieldActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(TextField));



// WEBPACK FOOTER //
// ./src/components/fields/TextField/TextField.js